import { ApolloClient, ApolloLink, InMemoryCache, createHttpLink, split, fromPromise } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';

// In production: set REACT_APP_API_URL to the Rathvan API gateway URL
// e.g. REACT_APP_API_URL=https://api.rathvan.com
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const SETU_ID  = process.env.REACT_APP_SETU_ID  || 'socialsetu';

const setuLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers }) => ({
    headers: { ...headers, 'X-Setu-Id': SETU_ID },
  }));
  return forward(operation);
});

const httpUrl = `${API_BASE}/graphql`;
const wsUrl = httpUrl.replace(/^https?/, (p) => (p === 'https' ? 'wss' : 'ws'));

const httpLink = createHttpLink({ uri: httpUrl });

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('hc_token');
  return { headers: { ...headers, authorization: token ? `Bearer ${token}` : '' } };
});

// Auto-refresh Cognito ID token on UNAUTHENTICATED errors
let refreshPromise = null;
async function doRefresh() {
  const refreshToken = localStorage.getItem('hc_refresh');
  if (!refreshToken) return null;
  try {
    const resp = await fetch(httpUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        operationName: 'RefreshToken',
        query: 'mutation RefreshToken($rt:String!){refreshToken(refreshToken:$rt){token refreshToken}}',
        variables: { rt: refreshToken },
      }),
    });
    const json = await resp.json();
    const result = json.data?.refreshToken;
    if (result?.token) {
      localStorage.setItem('hc_token', result.token);
      if (result.refreshToken) localStorage.setItem('hc_refresh', result.refreshToken);
      return result.token;
    }
  } catch {}
  // Refresh failed — clear session so user sees login prompt
  localStorage.removeItem('hc_token');
  localStorage.removeItem('hc_refresh');
  localStorage.removeItem('hc_user');
  return null;
}

const refreshLink = onError(({ graphQLErrors, operation, forward }) => {
  if (!graphQLErrors?.some(e => e.extensions?.code === 'UNAUTHENTICATED')) return;
  if (!localStorage.getItem('hc_refresh')) return;
  if (!refreshPromise) refreshPromise = doRefresh().finally(() => { refreshPromise = null; });
  return fromPromise(refreshPromise).flatMap(token => {
    if (!token) return forward(operation); // let it fail — user will be redirected to login
    operation.setContext(({ headers }) => ({
      headers: { ...headers, authorization: `Bearer ${token}` },
    }));
    return forward(operation);
  });
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: wsUrl,
    connectionParams: () => {
      const token = localStorage.getItem('hc_token');
      return { Authorization: token ? `Bearer ${token}` : '' };
    },
  })
);

const splitLink = split(
  ({ query }) => {
    const def = getMainDefinition(query);
    return def.kind === 'OperationDefinition' && def.operation === 'subscription';
  },
  wsLink,
  setuLink.concat(refreshLink.concat(authLink.concat(httpLink)))
);

export const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache({
    typePolicies: {
      Group: {
        fields: {
          messages: { merge: (_, incoming) => incoming },
          members: { merge: (_, incoming) => incoming },
        },
      },
    },
  }),
});
